import React, { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { HStack, Modal } from '@musinsa/mwms-ds'
import Button from '@/components/content/Button'
import Tooltip from '@/components/content/Tooltip'
import MAlert from '@/utils/modal/MAlert'
import MToast from '@/utils/modal/MToast'
import { Utils } from '@/utils/Utils'
import { AxiosError } from 'axios'
import { postPromotionBulkResult, postPromotionBulkRegister, postPromotionBulkValidate } from '@/services/settlement/settlementPromotion/api'
import { PromotionBulkValidateRes, PromotionBulkValidateRow, UnitType } from '@/services/settlement/settlementPromotion/types'
import { useGetServiceTypeList, usePostMFSPartnerList } from '@/services/settlement/settlementPromotion/useSettlementPromotionQueries'
import { formatChargeItemLabel } from '@/services/settlement/settlementPromotion/serviceChargeItem'
import { withUnknownPartnerErrors } from '@/services/settlement/settlementPromotion/bulkPartnerCheck'
import { joinOrDash, toPromotionKindLabel } from '@/pages/settlement/settlementPromotion/v2/schemas/postPromotionSchemas'
import { stripTrailingZeros, toDiscountDisplay } from '@/services/settlement/settlementPromotion/discountValue'

interface Props {
  open: boolean
  onClose: () => void
  refetch: () => void
}

// 1회 업로드 상한 — 시안 안내 문구와 같은 값
const MAX_ROWS = 1000

// 텍스트 열 픽셀 폭 — 업체·브랜드·카테고리는 좁게, 프로모션명은 넓게 (LOGISPRD-11504)
const BULK_COL_WIDTH_CODES = '160px'
const BULK_COL_WIDTH_TITLE = '220px'

// 검증 표 열 — 업로드 양식의 열 순서를 그대로 따른다. 사용자가 고쳐야 할 칸을 양식에서 바로 찾게 하려는 것이라
// 순서가 어긋나면 안 된다 (LOGISPRD-11320)
// maxWidth 가 있으면 그 폭에서 말줄임(...) 하고 넘칠 때만 호버 툴팁을 띄운다 (LOGISPRD-11492)
type ResultColumn = { key: string; label: string; maxWidth?: string; render: (row: PromotionBulkValidateRow) => string }

// 서비스 항목은 코드로 내려오므로 목록 API 의 이름으로 바꿔 보여 준다 (화면 다른 곳과 같은 표기)
const buildResultColumns = (serviceTypeName: (code?: string, unitType?: UnitType) => string): ResultColumn[] =>
  RESULT_COLUMNS.map((column) =>
    column.key === 'serviceType'
      ? { ...column, render: (row: PromotionBulkValidateRow) => serviceTypeName(row.value.serviceType, row.value.unitType) }
      : column,
  )

const RESULT_COLUMNS: ResultColumn[] = [
  { key: 'rowNo', label: '행', render: (row) => String(row.rowNo) },
  { key: 'country', label: '국가', render: (row) => (row.value.country === 'JP' ? '일본' : row.value.country === 'KR' ? '한국' : '-') },
  {
    key: 'orderType',
    label: '주문유형',
    render: (row) => (row.value.orderType === 'GLOBAL' ? '해외' : row.value.orderType === 'DOMESTIC' ? '국내' : '-'),
  },
  { key: 'promotionKind', label: '프로모션 종류', render: (row) => toPromotionKindLabel(row.value.kind) },
  { key: 'partnerIds', label: '업체', maxWidth: BULK_COL_WIDTH_CODES, render: (row) => joinOrDash(row.value.partnerIds) },
  { key: 'brands', label: '브랜드', maxWidth: BULK_COL_WIDTH_CODES, render: (row) => joinOrDash(row.value.brands) },
  { key: 'categoryCodes', label: '카테고리', maxWidth: BULK_COL_WIDTH_CODES, render: (row) => joinOrDash(row.value.categoryCodes) },
  // 양식에서 카테고리 오른쪽 열이다. 종류가 상품일 때만 값이 있다 (LOGISPRD-14476)
  { key: 'skuIds', label: '상품(SKU)', maxWidth: BULK_COL_WIDTH_CODES, render: (row) => joinOrDash(row.value.skuIds) },
  { key: 'serviceType', label: '서비스 항목', render: (row) => row.value.serviceType ?? '-' },
  { key: 'title', label: '프로모션명', maxWidth: BULK_COL_WIDTH_TITLE, render: (row) => row.value.title ?? '-' },
  { key: 'settlementCycle', label: '정산 방식', render: (row) => (row.value.settlementCycle === 'MONTHLY' ? '기간' : '일별') },
  { key: 'discountType', label: '할인 방식', render: (row) => (row.value.discountType === 'FIXED' ? '정액' : '정률') },
  { key: 'discountValue', label: '할인값', render: (row) => toDiscountLabel(row) },
  { key: 'startDate', label: '시작일', render: (row) => row.value.startDate ?? '-' },
  { key: 'endDate', label: '종료일', render: (row) => row.value.endDate ?? '-' },
  { key: 'remark', label: '비고', render: (row) => row.value.remark || '-' },
  { key: 'result', label: '검증 결과', render: (row) => (row.valid ? '정상' : row.errors.map((error) => error.message).join(' / ')) },
]

// 셀 표기 상한 — 넘으면 잘라서 표가 오른쪽으로 밀리지 않게 하고, 전체는 호버 툴팁으로 본다 (LOGISPRD-11492)
const CELL_MAX_CHARS = 50

function ResultCell({ text, maxWidth }: { text: string; maxWidth?: string }) {
  if (maxWidth) return <EllipsisCell text={text} maxWidth={maxWidth} />
  if (text.length <= CELL_MAX_CHARS) return <>{text}</>
  return (
    <Tooltip label={text} width="360px" whiteSpace="normal">
      <TruncatedCell>{`${text.slice(0, CELL_MAX_CHARS)}...`}</TruncatedCell>
    </Tooltip>
  )
}

// 픽셀 폭에서 말줄임 — 글자 수와 무관하게 열 폭을 고정한다. 실제로 넘친 셀만 호버 툴팁을 붙인다.
// 넘침 판정은 첫 호버 때 한 번만(scrollWidth > clientWidth) — 1000행 × 열을 마운트 때 재는 비용을 피한다.
// 넘친다고 판정되면 initShow 로 그 첫 호버부터 바로 뜬다 (다시 감싸느라 첫 호버를 놓치지 않게).
function EllipsisCell({ text, maxWidth }: { text: string; maxWidth: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const [overflowed, setOverflowed] = useState(false)

  if (overflowed) {
    return (
      <Tooltip label={text} width="360px" whiteSpace="normal" initShow>
        <TruncatedText $maxWidth={maxWidth} $help>
          {text}
        </TruncatedText>
      </Tooltip>
    )
  }
  return (
    <TruncatedText
      ref={ref}
      $maxWidth={maxWidth}
      onMouseEnter={() => {
        const el = ref.current
        if (el && el.scrollWidth > el.clientWidth) setOverflowed(true)
      }}
    >
      {text}
    </TruncatedText>
  )
}

// 서버가 준 파일명을 그대로 쓴다 (RFC 5987). 못 읽으면 고정 이름으로 떨어뜨린다
const toDownloadFileName = (disposition?: string) => {
  const matched = /filename\*=UTF-8''([^;]+)/i.exec(disposition ?? '')
  return matched ? decodeURIComponent(matched[1]) : 'MFS프로모션일괄등록_검증결과.xlsx'
}

const toDiscountLabel = (row: PromotionBulkValidateRow) => {
  if (row.value.settlementCycle === 'MONTHLY') return row.value.tiers?.length ? `구간 ${row.value.tiers.length}개` : '-'
  // 할인 방식만 읽히고 할인값 칸이 비면 BE 가 null 로 내려준다(오류 행). null 을 그대로 포맷하면 표가 죽는다
  if (row.value.discountValue == null) return '-'
  // 값은 문자열이다 — Number 로 바꾸면 정액 16자리가 반올림된다 (LOGISPRD-11361)
  // 소수부 뒤 0 은 떼고 보여 준다 — 목록·정산 상세와 같은 표기 (LOGISPRD-11422)
  const value = stripTrailingZeros(String(row.value.discountValue))
  return row.value.discountType === 'FIXED' ? `${toDiscountDisplay(value, false)}원` : `${value}%`
}

function PromotionBulkUploadModal({ open, onClose, refetch }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [serverResult, setServerResult] = useState<PromotionBulkValidateRes | null>(null)
  const [isValidating, setIsValidating] = useState(false)
  const [isRegistering, setIsRegistering] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  // 등록 화면 셀렉트와 같은 원천 — react-query 캐시를 공유해 호출이 새로 나가지 않는다
  const { data: mfsPartnerIds } = usePostMFSPartnerList()
  const knownPartners = useMemo(() => new Set(mfsPartnerIds ?? []), [mfsPartnerIds])
  const result = useMemo(
    () => (serverResult ? withUnknownPartnerErrors(serverResult, knownPartners) : null),
    [serverResult, knownPartners],
  )

  // 서비스 항목 코드 → 화면 표기 (목록·등록 화면과 같은 이름을 쓴다)
  const { data: serviceTypeList } = useGetServiceTypeList()
  const columns = buildResultColumns((code, unitType) => {
    if (!code) return '-'
    const found = (serviceTypeList ?? []).find((item) => item.code === code)
    return found ? formatChargeItemLabel(found.code, found.name, unitType ?? 'PCS') : code
  })

  const hasError = (result?.summary.invalid ?? 0) > 0
  const step = !result ? 1 : hasError ? 2 : 3

  const reset = () => {
    setFile(null)
    setServerResult(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSelectFile = async (selected: File) => {
    setFile(selected)
    setServerResult(null)
    setIsValidating(true)
    try {
      const response = await postPromotionBulkValidate(selected)
      setServerResult(response.data)
    } catch (error) {
      MAlert.show(Utils.getApiErrMessage(error as AxiosError, '파일을 검증하지 못했습니다.'))
      reset()
    } finally {
      setIsValidating(false)
    }
  }

  // 전체 다운로드 — 올린 파일을 서버가 고쳐 내려준다. 안내 행·헤더 위치·드롭다운이 그대로 남고 맨 오른쪽에
  // 행별 검증 결과만 붙으므로, 사유를 보고 고쳐 그 파일을 바로 다시 올릴 수 있다.
  // 오류 행만 내리지 않는 이유는 등록이 전량 아니면 전량 취소라서다 — 정상 행이 빠지면 함께 등록되지 않는다.
  // 서버가 만든 파일이라 화면에서만 거른 업체아이디 오류(LOGISPRD-11448)는 안 담긴다 — 그 사유는 이 표에서만 보인다
  const handleDownloadResult = async () => {
    if (!file) return
    setIsDownloading(true)
    try {
      const response = await postPromotionBulkResult(file)
      const blobUrl = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = toDownloadFileName(response.headers?.['content-disposition'])
      link.click()
      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      MAlert.show(Utils.getApiErrMessage(error as AxiosError, '검증 결과를 내려받지 못했습니다.'))
    } finally {
      setIsDownloading(false)
    }
  }

  const handleRegister = async () => {
    if (!result || !file) return
    setIsRegistering(true)
    try {
      const response = await postPromotionBulkRegister(file)
      MToast.show(`프로모션 ${response.data.registeredCount}건을 등록했습니다`)
      refetch()
      handleClose()
    } catch (error) {
      // 등록 직전 재검증에서 걸린 건은 행별 사유를 함께 준다 — 표를 다시 그려야 어느 행인지 보인다
      const rejected = (error as AxiosError<{ data?: PromotionBulkValidateRes }>).response
      if (rejected?.status === 409 && rejected.data?.data) setServerResult(rejected.data.data)
      MAlert.show(Utils.getApiErrMessage(error as AxiosError, '일괄 등록에 실패했습니다.'))
    } finally {
      setIsRegistering(false)
    }
  }

  return (
    <Modal
      open={open}
      title="프로모션 엑셀 일괄 업로드"
      customWidth={960}
      onClose={handleClose}
      buttons={
        // 확정 등록은 검증을 통과했을 때만 나온다 (시안 — STEP 1 에는 취소만)
        result && !hasError
          ? [
              { kind: 'secondary', content: '취소', onClick: handleClose },
              { kind: 'primary', content: '확정 등록', disabled: isRegistering, onClick: handleRegister },
            ]
          : [{ kind: 'secondary', content: '취소', onClick: handleClose }]
      }
    >
      <Body>
        <Steps>
          <StepItem $active={step === 1}>STEP 1. 파일 선택</StepItem>
          <StepItem $active={step === 2}>STEP 2. 검증</StepItem>
          <StepItem $active={step === 3}>STEP 3. 등록</StepItem>
        </Steps>

        {!result && (
          <>
            <DropZone
              $dragging={isDragging}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault()
                setIsDragging(true)
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault()
                setIsDragging(false)
                const dropped = e.dataTransfer.files?.[0]
                if (dropped) handleSelectFile(dropped)
              }}
            >
              <DropIcon>📄</DropIcon>
              {isValidating ? (
                <>파일을 검증하고 있습니다.</>
              ) : (
                <>
                  엑셀 파일(.xlsx)을 이곳에 끌어놓거나 클릭해서 선택해 주세요.
                  <br />
                  <DropSub>
                    1회 최대 {MAX_ROWS.toLocaleString('ko-KR')}행 / 템플릿 양식만 업로드 가능
                    {file ? ` · 선택한 파일: ${file.name}` : ''}
                  </DropSub>
                </>
              )}
            </DropZone>
            <Banner>
              업로드 즉시 등록되지 않습니다. 행 단위 검증을 먼저 수행합니다.
              <br />
              <b>오류가 1건이라도 있으면 전량 등록되지 않습니다(All or Nothing).</b> 오류를 모두 수정한 뒤 다시 업로드해 주세요.
            </Banner>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              onChange={(e) => {
                const selected = e.target.files?.[0]
                if (selected) handleSelectFile(selected)
              }}
            />
          </>
        )}

        {result && (
          <>
            <Summary>
              <SummaryItem>
                전체 <b>{result.summary.total}</b>
              </SummaryItem>
              <SummaryItem>
                정상 <b className="ok">{result.summary.valid}</b>
              </SummaryItem>
              <SummaryItem>
                오류 <b className="ng">{result.summary.invalid}</b>
              </SummaryItem>
            </Summary>

            <Banner $tone={hasError ? 'warn' : 'info'}>
              {hasError ? (
                <>
                  오류 {result.summary.invalid}건이 있어 <b>전량 등록되지 않습니다(All or Nothing)</b>. [전체 다운로드]로 받은 파일의 검증 결과를
                  보고 수정해 다시 업로드해 주세요.
                </>
              ) : (
                <>
                  검증을 모두 통과했습니다. <b>확정 등록</b>을 누르면 {result.summary.total}건이 한 번에 등록됩니다.
                </>
              )}
            </Banner>

            <HStack gap={8} justify="flex-end" style={{ marginBottom: 9 }}>
              <Button fit size="sm" kind="secondary" type="button" onClick={reset}>
                다시 업로드
              </Button>
              <Button fit size="sm" kind="secondary" type="button" disabled={isDownloading} onClick={handleDownloadResult}>
                전체 다운로드
              </Button>
            </HStack>

            <ResultTableWrap>
              <ResultTable>
                <thead>
                  <tr>
                    {columns.map((column) => (
                      <th key={column.key}>{column.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row) => (
                    <tr key={row.rowNo} className={row.valid ? undefined : 'invalid'}>
                      {columns.map((column) => (
                        <td key={column.key}>
                          <ResultCell text={column.render(row)} maxWidth={column.maxWidth} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </ResultTable>
            </ResultTableWrap>
          </>
        )}
      </Body>
    </Modal>
  )
}

const Body = styled.div`
  font-size: 13.5px;
  color: #333;
`

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 14px;
`

const StepItem = styled.div<{ $active: boolean }>`
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ $active }) => ($active ? '#1a1a1a' : '#e0e0e0')};
  background: ${({ $active }) => ($active ? '#1a1a1a' : '#fff')};
  color: ${({ $active }) => ($active ? '#fff' : '#999')};
  border-radius: 2px;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
`

const DropZone = styled.div<{ $dragging: boolean }>`
  border: 1px dashed ${({ $dragging }) => ($dragging ? '#1a1a1a' : '#d9d9d9')};
  background: ${({ $dragging }) => ($dragging ? '#f5f5f5' : '#fbfbfb')};
  border-radius: 3px;
  padding: 34px 16px;
  text-align: center;
  color: #666;
  font-size: 13px;
  line-height: 1.7;
  cursor: pointer;
`

const DropIcon = styled.div`
  font-size: 30px;
  margin-bottom: 8px;
`

const DropSub = styled.span`
  font-size: 11.5px;
  color: #999;
`

const Banner = styled.div<{ $tone?: 'info' | 'warn' }>`
  margin-top: 12px;
  border-radius: 3px;
  padding: 11px 12px;
  font-size: 12.5px;
  line-height: 1.7;
  border: 1px solid ${({ $tone }) => ($tone === 'warn' ? '#f4c7c3' : '#dbe7f3')};
  background: ${({ $tone }) => ($tone === 'warn' ? '#fdf3f2' : '#f4f8fc')};
  color: ${({ $tone }) => ($tone === 'warn' ? '#b42318' : '#33526e')};
  margin-bottom: 12px;
`

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 12px;
`

const SummaryItem = styled.div`
  border: 1px solid #eee;
  border-radius: 3px;
  padding: 12px;
  text-align: center;
  font-size: 12.5px;
  color: #888;

  b {
    display: block;
    margin-top: 4px;
    font-size: 18px;
    color: #333;
  }

  b.ok {
    color: #1a7f37;
  }

  b.ng {
    color: #d92d20;
  }
`

const ResultTableWrap = styled.div`
  max-height: 360px;
  overflow: auto;
  border: 1px solid #eee;
  border-radius: 3px;
`

const ResultTable = styled.table`
  /* 내용만큼 넓히고 모자라면 감싼 쪽이 가로로 스크롤한다.
     width:100% 로 두면 열이 눌려 nowrap 글자가 옆 칸을 덮는다 (LOGISPRD-11320) */
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
  white-space: nowrap;

  th,
  td {
    padding: 7px 9px;
    border-bottom: 1px solid #f0f0f0;
    text-align: left;
  }

  th {
    position: sticky;
    top: 0;
    background: #fafafa;
    font-weight: 700;
    color: #555;
  }

  tr.invalid td {
    background: #fdf3f2;
  }
`

// 잘린 셀 — 전체 내용은 호버 툴팁으로 본다는 걸 커서로 알린다
const TruncatedCell = styled.span`
  cursor: help;
`

// 픽셀 폭 말줄임 셀 — max-width 로 열 폭을 고정하고 넘치면 ...
// cursor: help 는 실제로 넘쳐 툴팁이 붙는 셀($help)에만 — 안 넘친 셀에 도움말 커서를 보이면 호버해도 툴팁이 없어 혼란
const TruncatedText = styled.span<{ $maxWidth: string; $help?: boolean }>`
  display: inline-block;
  max-width: ${({ $maxWidth }) => $maxWidth};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
  cursor: ${({ $help }) => ($help ? 'help' : 'default')};
`

export default PromotionBulkUploadModal
